import { useSyncExternalStore } from "react";
import { useInfiniteQuery, useMutation, useQuery, type InfiniteData } from "@tanstack/react-query";
import type { ChatChannel, ChatFile, ChatMessage, ChatPerson, Presence } from "@dfws/contracts";
import { api, ApiFailure, getCsrf } from "../../lib/api";
import { queryClient } from "../../lib/queries";
import { getSettings } from "../../settings/store";

export type UiMessage = ChatMessage & { pending?: boolean; failed?: boolean };
type MessagePage = { items: UiMessage[]; hasMore: boolean };
type Pages = InfiniteData<MessagePage, unknown>;
type People = { items: ChatPerson[]; me: { email: string; name: string } };

export const chatKeys = {
  people: ["chat", "people"] as const,
  channels: ["chat", "channels"] as const,
  messages: (id: string) => ["chat", "messages", id] as const,
};

export function useChatPeople(enabled = true) {
  return useQuery({ queryKey: chatKeys.people, queryFn: () => api<People>("/chat/people"), staleTime: 5 * 60_000, enabled });
}

export function useChannels(enabled = true) {
  return useQuery({ queryKey: chatKeys.channels, queryFn: () => api<{ items: ChatChannel[] }>("/chat/channels"), staleTime: 60_000, enabled });
}

export function useChannel(id: string | undefined) {
  const channels = useChannels();
  return channels.data?.items.find((c) => c.id === id);
}

export function useMessages(channelId: string) {
  return useInfiniteQuery({
    queryKey: chatKeys.messages(channelId),
    queryFn: ({ pageParam }) => api<MessagePage>(`/chat/channels/${channelId}/messages?limit=50${pageParam ? `&before=${pageParam}` : ""}`),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => (last.hasMore ? last.items[0]?.id : undefined),
    staleTime: Infinity, // the socket keeps it current; a reconnect refetches
  });
}

/** Oldest first, across all loaded pages. */
export function flatten(data: Pages | undefined): UiMessage[] {
  if (!data) return [];
  return data.pages.slice().reverse().flatMap((p) => p.items);
}

export function nameOf(email: string, people: People | undefined): string {
  if (people?.me.email === email) return people.me.name;
  const p = people?.items.find((x) => x.email === email);
  if (p) return p.name;
  return email.split("@")[0] ?? email;
}

export function presenceOf(email: string, people: People | undefined): Presence {
  return people?.items.find((x) => x.email === email)?.presence ?? "offline";
}

export function totalUnread(channels: ChatChannel[] | undefined): number {
  return (channels ?? []).reduce((n, c) => n + (c.muted ? 0 : c.unread), 0);
}

// ---- cache edits shared by the socket and the mutations ----

function upsertMessage(m: UiMessage) {
  queryClient.setQueryData<Pages>(chatKeys.messages(m.channelId), (d) => {
    if (!d) return d;
    const all = d.pages.flatMap((p) => p.items);
    const at = all.findIndex((x) => x.id === m.id || (m.clientId && x.clientId === m.clientId));
    if (at >= 0) {
      return { ...d, pages: d.pages.map((p) => ({ ...p, items: p.items.map((x) => (x.id === m.id || (m.clientId && x.clientId === m.clientId) ? { ...m, pending: false, failed: false } : x)) })) };
    }
    const [first, ...rest] = d.pages;
    if (!first) return d;
    return { ...d, pages: [{ ...first, items: [...first.items, m] }, ...rest] };
  });
}

function patchChannel(id: string, fn: (c: ChatChannel) => ChatChannel) {
  queryClient.setQueryData<{ items: ChatChannel[] }>(chatKeys.channels, (d) => (d ? { items: d.items.map((c) => (c.id === id ? fn(c) : c)) } : d));
}

function bumpChannel(m: ChatMessage, me: string) {
  const d = queryClient.getQueryData<{ items: ChatChannel[] }>(chatKeys.channels);
  const c = d?.items.find((x) => x.id === m.channelId);
  if (!d || !c) {
    void queryClient.invalidateQueries({ queryKey: chatKeys.channels });
    return;
  }
  const seenHere = isVisible(m.channelId);
  const updated: ChatChannel = { ...c, lastMessage: m, unread: m.author === me || seenHere ? c.unread : c.unread + 1 };
  queryClient.setQueryData(chatKeys.channels, { items: [updated, ...d.items.filter((x) => x.id !== c.id)] });
}

// ---- conversations on screen, for unread and notifications ----

const visible = new Map<string, number>();
export function isVisible(channelId: string): boolean {
  return (visible.get(channelId) ?? 0) > 0 && document.visibilityState === "visible";
}
export function markVisible(channelId: string): () => void {
  visible.set(channelId, (visible.get(channelId) ?? 0) + 1);
  return () => visible.set(channelId, Math.max(0, (visible.get(channelId) ?? 1) - 1));
}

// ---- typing ----

// Who is typing where, with an expiry; the snapshot per channel is recomputed outside render so it stays stable.
const typing = new Map<string, Map<string, number>>();
const typingSnapshot = new Map<string, string[]>();
const typingListeners = new Set<() => void>();
const NOBODY: string[] = [];
function refreshTyping(channelId: string) {
  const now = Date.now();
  const m = typing.get(channelId);
  typingSnapshot.set(channelId, m ? [...m.entries()].filter(([, until]) => until > now).map(([e]) => e) : NOBODY);
  typingListeners.forEach((l) => l());
}
export function useTyping(channelId: string): string[] {
  return useSyncExternalStore(
    (l) => {
      typingListeners.add(l);
      return () => typingListeners.delete(l);
    },
    () => typingSnapshot.get(channelId) ?? NOBODY,
  );
}
function setTyping(channelId: string, email: string, on: boolean) {
  const m = typing.get(channelId) ?? new Map<string, number>();
  if (on) {
    m.set(email, Date.now() + 5000);
    setTimeout(() => refreshTyping(channelId), 5100);
  } else m.delete(email);
  typing.set(channelId, m);
  refreshTyping(channelId);
}

// ---- notifications ----

export type NotifyFn = (m: ChatMessage, channel: ChatChannel | undefined) => void;
let openFromNotification: (channelId: string) => void = () => {};
export function setNotificationOpener(fn: (channelId: string) => void) {
  openFromNotification = fn;
}

function notify(m: ChatMessage, me: string) {
  if (m.author === me || typeof Notification === "undefined" || Notification.permission !== "granted") return;
  if (!getSettings().chatNotifications) return;
  const channel = queryClient.getQueryData<{ items: ChatChannel[] }>(chatKeys.channels)?.items.find((c) => c.id === m.channelId);
  if (channel?.muted || isVisible(m.channelId)) return;
  const people = queryClient.getQueryData<People>(chatKeys.people);
  const who = nameOf(m.author, people);
  const n = new Notification(channel?.kind === "group" ? `${who} in ${channel.name}` : who, {
    body: m.body ? m.body.slice(0, 180) : m.file ? `Sent ${m.file.name}` : "",
    tag: m.channelId,
    icon: "/brand/icon-192.png",
  });
  n.onclick = () => {
    window.focus();
    openFromNotification(m.channelId);
    n.close();
  };
}

// ---- socket events ----

export function onChatEvent(ev: { type?: string } & Record<string, unknown>, me: string) {
  switch (ev.type) {
    case "chat.message": {
      const m = ev.message as ChatMessage;
      setTyping(m.channelId, m.author, false);
      upsertMessage(m);
      bumpChannel(m, me);
      notify(m, me);
      break;
    }
    case "chat.message.updated": {
      const m = ev.message as ChatMessage;
      upsertMessage(m);
      patchChannel(m.channelId, (c) => (c.lastMessage?.id === m.id ? { ...c, lastMessage: m } : c));
      break;
    }
    case "chat.read": {
      const { channelId, email, upTo } = ev as unknown as { channelId: string; email: string; upTo: number };
      patchChannel(channelId, (c) => ({ ...c, readUpTo: { ...c.readUpTo, [email]: upTo }, unread: email === me ? unreadAfter(channelId, upTo, me) : c.unread }));
      break;
    }
    case "chat.typing": {
      const { channelId, email } = ev as unknown as { channelId: string; email: string };
      if (email !== me) setTyping(channelId, email, true);
      break;
    }
    case "chat.presence": {
      const { email, presence } = ev as unknown as { email: string; presence: Presence };
      queryClient.setQueryData<People>(chatKeys.people, (d) => (d ? { ...d, items: d.items.map((p) => (p.email === email ? { ...p, presence } : p)) } : d));
      break;
    }
    case "chat.channel": {
      const channel = ev.channel as ChatChannel;
      queryClient.setQueryData<{ items: ChatChannel[] }>(chatKeys.channels, (d) => {
        if (!d) return d;
        return d.items.some((c) => c.id === channel.id) ? { items: d.items.map((c) => (c.id === channel.id ? channel : c)) } : { items: [channel, ...d.items] };
      });
      break;
    }
    case "chat.left": {
      const channelId = ev.channelId as string;
      queryClient.setQueryData<{ items: ChatChannel[] }>(chatKeys.channels, (d) => (d ? { items: d.items.filter((c) => c.id !== channelId) } : d));
      break;
    }
  }
}

/** Unread left after reading up to a message, counted from what is loaded (the server count arrives on next fetch). */
function unreadAfter(channelId: string, upTo: number, me: string): number {
  const msgs = flatten(queryClient.getQueryData<Pages>(chatKeys.messages(channelId)));
  return msgs.filter((m) => m.id > upTo && m.author !== me && !m.deleted && !m.pending).length;
}

/** After a reconnect, anything may have been missed: refetch what is on screen. */
export function onChatReconnect() {
  void queryClient.invalidateQueries({ queryKey: ["chat"] });
}

// ---- mutations ----

let tempId = -1;

export function useSendMessage(channelId: string, me: string) {
  return useMutation({
    mutationFn: (input: { body: string; replyTo?: number; file?: ChatFile; clientId: string }) =>
      api<ChatMessage>(`/chat/channels/${channelId}/messages`, { method: "POST", body: { body: input.body, replyTo: input.replyTo, fileId: input.file?.id, clientId: input.clientId } }),
    onMutate: (input) => {
      upsertMessage({
        id: tempId--,
        channelId,
        author: me,
        body: input.body,
        createdAt: new Date().toISOString(),
        editedAt: null,
        deleted: false,
        replyTo: input.replyTo ?? null,
        file: input.file ?? null,
        clientId: input.clientId,
        pending: true,
      });
    },
    onSuccess: (m) => {
      upsertMessage(m);
      bumpChannel(m, me);
    },
    onError: (_e, input) => {
      queryClient.setQueryData<Pages>(chatKeys.messages(channelId), (d) =>
        d ? { ...d, pages: d.pages.map((p) => ({ ...p, items: p.items.map((x) => (x.clientId === input.clientId ? { ...x, pending: false, failed: true } : x)) })) } : d,
      );
    },
  });
}

export function removeFailed(channelId: string, clientId: string) {
  queryClient.setQueryData<Pages>(chatKeys.messages(channelId), (d) => (d ? { ...d, pages: d.pages.map((p) => ({ ...p, items: p.items.filter((x) => x.clientId !== clientId) })) } : d));
}

export async function editMessage(id: number, body: string) {
  upsertMessage(await api<ChatMessage>(`/chat/messages/${id}`, { method: "PATCH", body: { body } }));
}

export async function deleteMessage(id: number) {
  upsertMessage(await api<ChatMessage>(`/chat/messages/${id}`, { method: "DELETE" }));
}

const lastReadSent = new Map<string, number>();
export function markRead(channelId: string, upTo: number) {
  if (upTo <= 0 || (lastReadSent.get(channelId) ?? 0) >= upTo) return;
  lastReadSent.set(channelId, upTo);
  patchChannel(channelId, (c) => ({ ...c, unread: 0 }));
  void api(`/chat/channels/${channelId}/read`, { method: "POST", body: { upTo } }).catch(() => lastReadSent.delete(channelId));
}

export async function openDm(email: string): Promise<ChatChannel> {
  const c = await api<ChatChannel>("/chat/dm", { method: "POST", body: { email } });
  queryClient.setQueryData<{ items: ChatChannel[] }>(chatKeys.channels, (d) => (d && !d.items.some((x) => x.id === c.id) ? { items: [c, ...d.items] } : d));
  return c;
}

export async function createGroup(name: string, members: string[]): Promise<ChatChannel> {
  const c = await api<ChatChannel>("/chat/groups", { method: "POST", body: { name, members } });
  queryClient.setQueryData<{ items: ChatChannel[] }>(chatKeys.channels, (d) => (d && !d.items.some((x) => x.id === c.id) ? { items: [c, ...d.items] } : d));
  return c;
}

export async function updateChannel(id: string, patch: { name?: string; topic?: string; muted?: boolean; addMembers?: string[] }) {
  const c = await api<ChatChannel>(`/chat/channels/${id}`, { method: "PATCH", body: patch });
  patchChannel(id, () => c);
}

export async function leaveChannel(id: string) {
  await api(`/chat/channels/${id}/leave`, { method: "POST" });
  queryClient.setQueryData<{ items: ChatChannel[] }>(chatKeys.channels, (d) => (d ? { items: d.items.filter((c) => c.id !== id) } : d));
}

/** Uploads with progress (fetch cannot report upload progress). */
export function uploadFile(channelId: string, file: File, onProgress: (fraction: number) => void): Promise<ChatFile> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/v1/chat/channels/${channelId}/files`);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.setRequestHeader("X-File-Name", encodeURIComponent(file.name));
    xhr.setRequestHeader("X-File-Type", file.type || "application/octet-stream");
    xhr.setRequestHeader("X-CSRF-Token", getCsrf());
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        /* not JSON */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as ChatFile);
      else reject(new ApiFailure(xhr.status, (body as { error?: { code: string; message: string; retryable: boolean } } | null)?.error ?? null));
    };
    xhr.onerror = () => reject(new ApiFailure(0, { code: "network", message: "The upload failed. Check your connection.", retryable: true }));
    xhr.send(file);
  });
}

export function fileUrl(id: string, download = false): string {
  return `/api/v1/chat/files/${id}${download ? "?download" : ""}`;
}
